import {AfterViewInit, Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {Tile, TileId} from '../../../../../shared/models/tile';
import {BehaviorSubject, forkJoin, of, Subscription} from 'rxjs';
import {SortTable, SortTableRow} from '../../../../../shared/models/sort-table/types';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {SearchService} from '../../../../../core/services/search.service';
import {TilesService} from '../../../../../core/services/tiles.service';
import {AlarmsService} from '../../../../../core/services/alarms.service';
import {AlarmEvent} from '../../../../../shared/models/alarm';
import {haveSameTileAlarms, mapActiveTileAlarms} from '../../../../../shared/utils/alarm.util';
import {
  SortTableCellButtons,
  SortTableCellCurrentValue,
  SortTableCellEmpty,
  SortTableCellIcon,
  SortTableCellKebabMenu,
  SortTableCellTags,
  SortTableCellTextPill,
  SortTableCellTitleSharingInfo,
} from '../../../../../shared/models/sort-table/cell-classes';
import {catchError, debounceTime, map, switchMap, take} from 'rxjs/operators';

import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {Option, OptionGroupMultiSelect} from '../../../../../shared/models/option';
import {SessionService} from '../../../../../core/services/session.service';
import {BaseTableViewComponent} from '../../../../components/_base-table-view/base-table-view.component';
import {UrlService} from '../../../../../core/services/url.service';
import {TitleChangedAction} from '../../../../../core/actions/title.actions';
import {SubtitleItem} from '../../../../../core/state/title.state';
import {Filters} from '../../../../../shared/models/filter';
import {ObjectModel, ObjectType} from '../../../../../shared/models/object.model';
import {SpinnerComponent} from '../../../../components/spinner/spinner.component';
import {SortTableComponent} from '../../../../components/sort-table/sort-table.component';
import {TileEditorComponent} from '../../../../components/tile-editor/tile-editor.component';
import {SchedulesViewerComponent} from '../../../../components/schedules-viewer/schedules-viewer.component';
import {
  COL_IDX_BUTTONS,
  COL_IDX_CURRENT_VALUE,
  COL_IDX_ICON,
  COL_IDX_ID,
  COL_IDX_NAME,
  COL_IDX_TAGS,
  TILES_EXISTING_COL_IDX_KEBAB,
} from 'src/shared/constants/tiles-existing-table-indexes';
import {DialogBoxButtonColor} from 'src/shared/models/dialog-box';
import {Permission} from '../../../../../shared/models/fga/permission';
import {PermissionsService} from '../../../../../core/services/permissions.service';
import {ButtonConfig} from '../../../../../shared/models/button';
import {EMPTY_ID} from '../../../../../shared/constants/empty-id';
import {TableMenuOptionId, TileMenuOptionId} from '../../../../../shared/constants/menu-option-ids';
import {MotiChatService} from '../../../../../moti/services/moti-chat.service';
import {TileHistoryService} from '../../../../../core/services/tile-history.service';
import {BulkModalService} from '@features/tile-bulk-operations/shared/services/bulk-modal.service';
import {FeatureFlagsService, FeatureFlag} from '@core/services/feature-flags.service';
import {TagService} from '../../../../../core/services/tag.service';
import {sortByProfileOrder} from '../../../../../shared/constants/tag-profiles.constants';

@Component({
  selector: 'motn-tiles-existing',
  templateUrl: './tiles-existing.component.html',
  styleUrls: ['./tiles-existing.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    SpinnerComponent,
    SortTableComponent,
    TileEditorComponent,
    SchedulesViewerComponent,
  ],
})
export class TilesExistingComponent extends BaseTableViewComponent implements OnInit, OnDestroy, AfterViewInit {
  tiles: Tile[];
  searchTerm: string = '';
  filters: Filters;
  tagSearchResultIds: Set<TileId> | null = null;
  editingTile: TileId = EMPTY_ID;
  schedulingTile: Tile;

  private tilesSub: Subscription;
  private searchSub: Subscription;
  private filtersSub: Subscription;
  private metadataSearchSub: Subscription;
  private tagsSub: Subscription;
  private alarmsSub: Subscription;
  private tileAlarms = new Map<TileId, AlarmEvent>();
  private userTilesLoaded = false;

  _updateTableDebounce$: BehaviorSubject<null> = new BehaviorSubject<null>(null);

  private currentOptionGroup: OptionGroupMultiSelect;


  constructor(
    private store: Store<AppState>,
    private tilesService: TilesService,
    private searchService: SearchService,
    private urlService: UrlService,
    private shadeTools: ShadeToolsService,
    private session: SessionService,
    private permissionsService: PermissionsService,
    private motiChatService: MotiChatService,
    private tileHistoryService: TileHistoryService,
    private bulkModalService: BulkModalService,
    private featureFlagsService: FeatureFlagsService,
    private tagService: TagService,
    private alarmsService: AlarmsService,
  ) {
    super();
  }


  ngOnInit(): void {
    this.searchService.enableSearch();
    this.searchService.enableFilters({
      by: {
        devices: true,
        hubs: false,
        owners: true,
        metadata: true,
        tags: true,
      },
    });

    this._updateTableDebounce$.pipe(
      debounceTime(150),
    ).subscribe({
      next: (_) => {
        this._updateTable();
      },
    });
  }


  ngAfterViewInit() {
    this.alarmsSub = this.alarmsService.activeEvents$.subscribe({
      next: (events) => {
        const alarms = mapActiveTileAlarms(events);

        if (!haveSameTileAlarms(this.tileAlarms, alarms)) {
          this.tileAlarms = alarms;
          this.updateTable();
        }
      },
    });

    this.searchSub = this.urlService.searchTerm$.subscribe({
      next: (term) => {
        this.searchTerm = term;
        this.updateTable();
      },
    });

    this.filtersSub = this.urlService.filters$.pipe(
      switchMap(filters => {
        if (filters?.tagFilters?.Filters?.length) {
          return this.tagService.searchObjects$(ObjectType.TILE, filters.tagFilters).pipe(
            map(results => ({filters, resultIds: new Set(results.map(r => r.ID)) as Set<TileId>})),
            catchError(() => of({filters, resultIds: null as Set<TileId> | null})),
          );
        }
        return of({filters, resultIds: null as Set<TileId> | null});
      }),
    ).subscribe(({filters, resultIds}) => {
      this.filters = filters;
      this.tagSearchResultIds = resultIds;
      this.updateTable();
    });

    this.metadataSearchSub = this.searchService.searchMetadataEnabled$.subscribe({
      next: (_enabled) => {
        // Re-run search when metadata toggle changes
        this.updateTable();
      },
    });

    this.tagsSub = this.tagService.getUserTags$().pipe(take(1)).subscribe({
      next: () => this.updateTable(),
    });

    this.tilesSub = this.store.select('tiles').subscribe(
      (state) => {
        const tiles = Object.values(state.all);

        for (let tile of tiles) {
          const row = this.tableRows[tile.ID];
          if (row) {
            this.upsertRowData(row, tile);
          }
        }

        if (this.userTilesLoaded) {
          this.tiles = tiles;
          this.updateTable();
        }
      },
    );

    forkJoin([
      this.tilesService.fetchUserTiles$().pipe(
        take(1),
      ),
    ]).subscribe(
      ([tiles]) => {
        this.loading = false;
        this.userTilesLoaded = true;
        this.tiles = tiles;
        this.updateTable();
      },
    );
  }


  ngOnDestroy() {
    this.tilesSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.filtersSub?.unsubscribe();
    this.metadataSearchSub?.unsubscribe();
    this.tagsSub?.unsubscribe();
    this.alarmsSub?.unsubscribe();

    this._updateTableDebounce$.complete();
  }


  /**
   *
   * @protected
   */
  protected updateTitle() {
    const subtitle: SubtitleItem[] = [];

    if (this.filters) {
      subtitle.push(
        {
          icon: 'filter',
          text: 'Filters applied',
        },
      );
    }

    if (this.searchTerm) {
      subtitle.push(
        {
          icon: `magnifying-glass`,
          text: `Search term entered`,
        },
      );
    }

    if (this.filters || this.searchTerm) {
      subtitle.push(
        {
          text: `${this.table$.getValue().rows.length.toLocaleString()} results`,
        },
      );
    } else {
      subtitle.push(
        {
          text: `${this.table$.getValue().rows.length.toLocaleString()} total`,
        },
      );
    }

    // Add bulk configure pill button (opens upload modal)
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_BULK_TILE_CONFIGURATION)) {
      subtitle.push({
        icon: 'wrench',
        text: 'Bulk Configure',
        callback: () => {
          this.openBulkConfigureModal();
          return {};
        },
      });
    }

    this.store.dispatch(new TitleChangedAction(
      '',
      'Tiles',
      subtitle,
    ));
  }


  tableRowsRendered(rows: SortTableRow[]) {
  }


  private deleteTile(tileId: TileId) {
    this.tilesService.deleteTile$(tileId).subscribe(
      () => {
        this.deletedTile(tileId);
        this.shadeTools.closeAll();
      },
      (err) => {
        this.shadeTools.errorDialog(err);
      },
    );
  }


  /**
   * Call this after some other process finished deleting a tile.
   *
   * @param tileId
   */
  deletedTile(tileId: TileId) {
    this.editingTile = EMPTY_ID;
    delete this.tableRows[tileId];
    this.updateTable();
  }


  onTileSaved() {
    this._updateTable();
  }


  protected updateTable() {
    this._updateTableDebounce$.next(null);
  }


  private _updateTable() {
    if (!this.tiles) {
      return;
    }

    const colKebabWidth = 40;
    const colNameWidth = 200;
    const colCurrentValueWidth = 150;
    const colIconWidth = 20;
    const colTagsWidth = 180;
    const colButtonsWidth = 230;
    const colIdWidth = 105;

    const table: SortTable = {
      css: {
        'min-width': (colIconWidth + colNameWidth + colCurrentValueWidth + colTagsWidth + colButtonsWidth + colIdWidth + colKebabWidth) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 50,
        startOnPage: 1,
        maxPageOptions: 12,
      },
    };

    {
      table.columns[TILES_EXISTING_COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': colKebabWidth + 'px',
          'justify-content': 'center',
        },
      };

      table.columns[COL_IDX_ICON] = {
        name: '',
        css: {
          'width': colIconWidth + 'px',
          'justify-content': 'center',
          'padding': '0',
        },
      };

      table.columns[COL_IDX_NAME] = {
        name: 'Tile Name',
        css: {
          'flex': 1,
        },
        sortingFn: SortTableCellTitleSharingInfo.sortingFnTitle,
      };

      table.columns[COL_IDX_TAGS] = {
        name: 'Tags',
        css: {
          'flex': 1,
          'min-width': colTagsWidth + 'px',
        },
      };

      table.columns[COL_IDX_CURRENT_VALUE] = {
        name: 'Current Value',
        css: {
          'width': colCurrentValueWidth + 'px',
        },
      };

      table.columns[COL_IDX_BUTTONS] = {
        name: '',
        css: {
          'width': colButtonsWidth + 'px',
          'justify-content': 'center',
        },
      };

      table.columns[COL_IDX_ID] = {
        name: 'ID',
        css: {
          'width': colIdWidth + 'px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellTextPill.sortingFnPillText,
        sortDefault: 'down',
      };
    }

    let tiles: Tile[] = this.tiles;

    if (this.searchTerm) {
      tiles = this.searchService.searchTiles(this.searchTerm, tiles);
    }

    if (this.filters) {
      const filtersForClient = this.tagSearchResultIds !== null
        ? {...this.filters, tagFilters: undefined}
        : this.filters;
      tiles = this.searchService.filterTiles(filtersForClient, tiles);
    }
    if (this.tagSearchResultIds !== null) {
      tiles = tiles.filter(t => this.tagSearchResultIds!.has(t.ID));
    }

    this.searchService.setResultQty(tiles.length);

    for (let tile of tiles) {
      let row: SortTableRow = this.tableRows[tile.ID];

      if (!this.tableRows[tile.ID]) {
        row = this.tableRows[tile.ID] = {
          context: '' + tile.ID,
          cells: [],
        };
      }

      this.upsertRowData(row, tile);
      table.rows.push(row);
    }

    this.table$.next(table);
    this.updateTitle();
  }


  private openTileConfiguration(tileId: TileId) {
    this.editingTile = tileId;
  }


  private openTileSchedules(tile: Tile) {
    this.schedulingTile = tile;
  }


  private openTileHistory(tileId: TileId) {
    // Get the tile from the tiles array
    const tile = this.tiles?.find(t => t.ID === tileId);
    if (tile && tile.typeSupportsTileHistory()) {
      this.tileHistoryService.addToDrawerTiles([tile]);
    }
  }


  /**
   * For inserting data into a brand new table row or updating an existing
   * table row.
   *
   * @param row
   * @param tile
   * @private
   */
  protected upsertRowData(row: SortTableRow, tile: Tile): void {
    if (!row.cells[COL_IDX_ID]) {
      row.cells[COL_IDX_ID] = new SortTableCellTextPill('', '' + tile.ID).setPillLetter('t');
    }

    if (!row.cells[COL_IDX_ICON]) {
      row.cells[COL_IDX_ICON] = new SortTableCellIcon(tile.Appearance.Icon);
    } else {
      (row.cells[COL_IDX_ICON] as SortTableCellIcon).icon = tile.Appearance.Icon;
    }

    if (!row.cells[COL_IDX_NAME]) {
      row.cells[COL_IDX_NAME] = new SortTableCellTitleSharingInfo({
        title: tile.Name,
        object: new ObjectModel({
          id: tile.ID,
          type: ObjectType.TILE,
          name: tile.Name,
          owner: tile.Owner,
        }),
        displayName: tile.Appearance.DisplayName,
      });
    } else {
      const _row = (row.cells[COL_IDX_NAME] as SortTableCellTitleSharingInfo);
      _row.title = tile.Name;
      _row.object.name = tile.Name;
      _row.displayName = tile.Appearance.DisplayName;
    }

    (row.cells[COL_IDX_NAME] as SortTableCellTitleSharingInfo).alarmEvent = this.tileAlarms.get(tile.ID) ?? null;

    if (!row.cells[COL_IDX_CURRENT_VALUE]) {
      // Only show current value for tiles that support reading values (not folders)
      if (tile.typeSupportsPolling()) {
        const currentValueCell = new SortTableCellCurrentValue(tile.ID);
        // Add click callback to open tile history panel
        currentValueCell.addClickCallback(() => {
          this.openTileHistory(tile.ID);
        });
        row.cells[COL_IDX_CURRENT_VALUE] = currentValueCell;
      } else {
        // For folder tiles, use an empty cell
        row.cells[COL_IDX_CURRENT_VALUE] = new SortTableCellEmpty();
      }
    }

    if (!row.cells[COL_IDX_TAGS]) {
      const tagsCell = new SortTableCellTags(tile.ID);
      row.cells[COL_IDX_TAGS] = tagsCell;
      tagsCell.loaded = true;
    }
    {
      (row.cells[COL_IDX_TAGS] as SortTableCellTags).tags = tile.tags;
      (row.cells[COL_IDX_NAME] as SortTableCellTitleSharingInfo).facetTags = sortByProfileOrder(tile.facets);
    }

    if (!row.cells[TILES_EXISTING_COL_IDX_KEBAB]) {
      // Max 5 options: Configure, Delete, Schedules, Chat, + Bulk Select
      const kebabCell = new SortTableCellKebabMenu(tile.ID, [], tile.displayName, true, 5);
      row.cells[TILES_EXISTING_COL_IDX_KEBAB] = kebabCell;

      // Add callback that will be called when kebab menu is opened
      kebabCell.addClickCallback(() => {
        if (!kebabCell.isLoading) {
          kebabCell.isLoading = true;

          this.permissionsService.queryPermissions({
            UserID: this.session.userId,
            ObjectID: tile.ID,
            ObjectType: ObjectType.TILE,
            Permissions: [
              Permission.CAN_CONFIGURE,
              Permission.CAN_DELETE,
              Permission.CAN_EDIT_SCHEDULES,
              Permission.CAN_CHAT,
            ],
          }).subscribe({
            next: (response) => {
              const kebabOptions: Option[] = [];

              // Configure
              if (response.Results.find(r => r.Permission === Permission.CAN_CONFIGURE)?.Allowed) {
                kebabOptions.push({
                  id: TableMenuOptionId.CONFIGURE,
                  name: 'Configure Tile',
                  callback: () => this.openTileConfiguration(tile.ID),
                });
              }

              // Delete
              if (response.Results.find(r => r.Permission === Permission.CAN_DELETE)?.Allowed) {
                kebabOptions.push({
                  id: TableMenuOptionId.DELETE,
                  name: 'Delete Tile',
                  callback: () => {
                    const displayNameTip = tile.Appearance.DisplayName ? `(${tile.Appearance.DisplayName})` : ``;
                    this.shadeTools.startDialog(
                      `Delete Tile ${tile.ID}`,
                      `Are you sure you want to delete the tile named ${tile.Name} ${displayNameTip}?`,
                      [
                        {
                          text: 'Yes, Delete Tile',
                          callback: () => {
                            this.deleteTile(tile.ID);
                          },
                          color: DialogBoxButtonColor.Red,
                        },
                        {
                          text: 'No, Go Back',
                          callback: () => {
                            this.shadeTools.closeAll();
                          },
                          color: DialogBoxButtonColor.Alt,
                        },
                      ],
                      false,
                      false,
                      false,
                      false,
                    );
                  },
                });
              }

              // Manage Schedules
              if (response.Results.find(r => r.Permission === Permission.CAN_EDIT_SCHEDULES)?.Allowed) {
                kebabOptions.push({
                  id: TileMenuOptionId.SCHEDULES,
                  name: 'Manage Schedules',
                  callback: () => this.openTileSchedules(tile),
                });
              }

              // Chat option
              if (response.Results.find(r => r.Permission === Permission.CAN_CHAT)?.Allowed) {
                kebabOptions.push({
                  id: TableMenuOptionId.CHAT,
                  name: 'Chat',
                  icon: 'fa-kit fa-chat',
                  callback: () => {
                    this.motiChatService.openChat(tile.ID, tile.Name, ObjectType.TILE);
                  },
                });
              }

              kebabCell.options = kebabOptions;
              kebabCell.isLoading = false;
            },
            error: () => {
              kebabCell.options = [];
              kebabCell.isLoading = false;
              this.shadeTools.errorDialog();
            },
          });
        }
      });
    } else {
      (row.cells[TILES_EXISTING_COL_IDX_KEBAB] as SortTableCellKebabMenu).menuTitle = tile.displayName;
    }

    if (!row.cells[COL_IDX_BUTTONS]) {
      const buttonsCell = new SortTableCellButtons([]);
      buttonsCell.buttons = this.getTileButtonConfigs(tile);
      row.cells[COL_IDX_BUTTONS] = buttonsCell;
    }
  }


  private setButtonLoading(tileId: TileId, buttonId: string, loading: boolean) {
    const row = this.tableRows[tileId];
    if (!row) {
      return;
    }
    const buttonsCell = row.cells[COL_IDX_BUTTONS] as SortTableCellButtons;
    const idx = buttonsCell.buttons.findIndex(b => b.id === buttonId);
    if (idx !== -1) {
      buttonsCell.buttons[idx] = {...buttonsCell.buttons[idx], loading};
    }
  }


  private withPermission(
    permission: Permission,
    tile: Tile,
    action: () => void,
    errorMsg: string,
  ): () => void {
    return () => {
      this.permissionsService.queryPermissions({
        UserID: this.session.userId,
        ObjectID: tile.ID,
        ObjectType: ObjectType.TILE,
        Permissions: [permission],
      }).subscribe({
        next: (response) => {
          if (response.Results[0].Allowed) {
            action();
          } else {
            this.setButtonLoading(tile.ID, 'conf', false);
            this.shadeTools.errorDialog(errorMsg);
          }
        },
        error: () => {
          this.setButtonLoading(tile.ID, 'conf', false);
          this.shadeTools.errorDialog();
        },
        complete: () => this.setButtonLoading(tile.ID, 'conf', false),
      });
    };
  }


  private getTileButtonConfigs(tile: Tile): ButtonConfig[] {
    return [
      {
        id: 'conf',
        text: 'Configure',
        colors: ['indigo'],
        callback: () => {
          this.setButtonLoading(tile.ID, 'conf', true);
          this.withPermission(
            Permission.CAN_CONFIGURE,
            tile,
            () => {
              this.setButtonLoading(tile.ID, 'conf', false);
              this.openTileConfiguration(tile.ID);
            },
            'You do not have permission to configure this tile.',
          )();
        },
      },
      {
        id: 'apps',
        text: 'Link/Unlink Apps',
        colors: ['indigo'],
        multiselect: {
          onReady: (optionGroup: OptionGroupMultiSelect) => {
            this.currentOptionGroup = optionGroup;
            optionGroup.saveInitialSelection();
            this.permissionsService.queryPermissions({
              UserID: this.session.userId,
              ObjectID: tile.ID,
              ObjectType: ObjectType.TILE,
              Permissions: [Permission.CAN_CONFIGURE],
            }).subscribe({
              next: (response) => {
                optionGroup.canConfigure = !!response.Results[0]?.Allowed;
              },
              error: () => {
                optionGroup.canConfigure = false;
              },
            });
          },
          onClickedOption: () => {
            if (this.currentOptionGroup?.canConfigure === false) {
              this.shadeTools.errorDialog('You do not have permission to link/unlink apps for this tile.');
              return;
            }
          },
          onClickedOutside: () => {
            const optionGroup = this.currentOptionGroup;

            if (!optionGroup) {
              this.shadeTools.closeAll();
              return;
            }

            this.currentOptionGroup = null;

            const currentlySelected = optionGroup.getSelected().map(app => app.id);
            const currentlyUnselected = optionGroup.getUnselected().map(app => app.id);

            if (currentlySelected.length === 0 && currentlyUnselected.length === 0) {
              this.shadeTools.closeAll();
              return;
            }

            this.withPermission(
              Permission.CAN_CONFIGURE,
              tile,
              () => {
                this.tilesService.updateAppLinks$(
                  tile.ID,
                  currentlySelected,
                  currentlyUnselected,
                ).subscribe({
                  next: () => this.shadeTools.closeAll(),
                  error: () => this.shadeTools.errorDialog(),
                });
              },
              'You do not have permission to link/unlink apps for this tile.',
            )();
          },
          objectType: {
            apps: {
              enabled: true,
              preselectAppsWithTileId: tile.ID,
            },
          },
        },
      },
    ];
  }


  /**
   * Open bulk configure modal (for CSV upload)
   */
  private openBulkConfigureModal() {
    this.bulkModalService.openConfigurationModal({});
  }
}
